import React from 'react';
import {
  Box,
  Typography,
  Grid,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormControlLabel,
  Switch,
  Chip,
  OutlinedInput,
} from '@mui/material';
import { TechnicalReport } from '../../types/PostProduction';

interface TechnicalReportSectionProps {
  technicalReport: TechnicalReport;
  editMode: boolean;
  onChange: (field: string, value: any) => void;
}

const TechnicalReportSection: React.FC<TechnicalReportSectionProps> = ({
  technicalReport,
  editMode,
  onChange,
}) => {
  // Setup issue categories from proposal
  const setupIssueOptions = [
    'Backline delays',
    'Instrument issues',
    'Stage setup',
    'Lighting setup',
    'Sound check timing',
    'Load-in access',
    'None',
    'Other'
  ];

  // Technical performance options from proposal
  const technicalPerformanceOptions = [
    { value: 'excellent', label: 'Excellent (no issues)' },
    { value: 'good', label: 'Good (minor issues resolved quickly)' },
    { value: 'fair', label: 'Fair (some difficulties)' },
    { value: 'poor', label: 'Poor (significant problems)' }
  ];

  // Issue areas from proposal
  const issueAreaOptions = [
    'Sound system',
    'Lighting',
    'Backline',
    'Stage',
    'Power/electrical',
    'Communication',
    'None'
  ];

  return (
    <Box>
      <Typography variant="h5" gutterBottom>
        Technical Report
      </Typography>
      <Grid container spacing={3}>
        <Grid item xs={12} md={6}>
          <Box sx={{ mb: 3 }}>
            <Typography component="legend">Sound Quality</Typography>
            <TextField
              type="number"
              fullWidth
              inputProps={{ min: 0, max: 5, step: 1 }}
              value={technicalReport.soundQuality}
              onChange={(e) => {
                const value = Number(e.target.value);
                if (value >= 0 && value <= 5) {
                  onChange('soundQuality', value);
                }
              }}
              disabled={!editMode}
              helperText="Rate 0-5"
            />
          </Box>
          <Box sx={{ mb: 3 }}>
            <Typography component="legend">Lighting Quality</Typography>
            <TextField
              type="number"
              fullWidth
              inputProps={{ min: 0, max: 5, step: 1 }}
              value={technicalReport.lightingQuality}
              onChange={(e) => {
                const value = Number(e.target.value);
                if (value >= 0 && value <= 5) {
                  onChange('lightingQuality', value);
                }
              }}
              disabled={!editMode}
              helperText="Rate 0-5"
            />
          </Box>
          <FormControl fullWidth sx={{ mb: 3 }}>
            <InputLabel>Backline Status</InputLabel>
            <Select
              value={technicalReport.backlineStatus}
              onChange={(e) => onChange('backlineStatus', e.target.value)}
              label="Backline Status"
              disabled={!editMode}
            >
              <MenuItem value="excellent">Simply Fabulous</MenuItem>
              <MenuItem value="good">All Good</MenuItem>
              <MenuItem value="fair">Needs Attention</MenuItem>
              <MenuItem value="poor">Issues to Report</MenuItem>
            </Select>
          </FormControl>

          {/* NEW: Technical Performance Dropdown */}
          <FormControl fullWidth sx={{ mb: 3 }}>
            <InputLabel>Technical Performance</InputLabel>
            <Select
              value={technicalReport.technicalPerformance || ''}
              onChange={(e) => onChange('technicalPerformance', e.target.value)}
              label="Technical Performance"
              disabled={!editMode}
            >
              {technicalPerformanceOptions.map((option) => (
                <MenuItem key={option.value} value={option.value}>
                  {option.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          {/* NEW: Issue Areas (Multiple Select) */}
          <FormControl fullWidth sx={{ mb: 3 }}>
            <InputLabel>Issue Areas</InputLabel>
            <Select
              multiple
              value={technicalReport.issueAreas || []}
              onChange={(e) => onChange('issueAreas', e.target.value)}
              input={<OutlinedInput label="Issue Areas" />}
              renderValue={(selected) => (
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                  {(selected as string[]).map((value) => (
                    <Chip key={value} label={value} size="small" />
                  ))}
                </Box>
              )}
              disabled={!editMode}
            >
              {issueAreaOptions.map((option) => (
                <MenuItem key={option} value={option}>
                  {option}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <FormControlLabel
            control={
              <Switch
                checked={technicalReport.maintenanceRequired}
                onChange={(e) => onChange('maintenanceRequired', e.target.checked)}
                disabled={!editMode}
              />
            }
            label="Maintenance Required"
          />
        </Grid>
        <Grid item xs={12} md={6}>
          <TextField
            label="Maintenance Notes"
            multiline
            rows={3}
            fullWidth
            value={technicalReport.maintenanceNotes}
            onChange={(e) => onChange('maintenanceNotes', e.target.value)}
            disabled={!editMode || !technicalReport.maintenanceRequired}
            margin="normal"
          />

          {/* NEW: Setup Issue Categories (Multiple Select) */}
          <FormControl fullWidth sx={{ mb: 2, mt: 2 }}>
            <InputLabel>Setup Issue Categories</InputLabel>
            <Select
              multiple
              value={technicalReport.setupIssueCategories || []}
              onChange={(e) => onChange('setupIssueCategories', e.target.value)}
              input={<OutlinedInput label="Setup Issue Categories" />}
              renderValue={(selected) => (
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                  {(selected as string[]).map((value) => (
                    <Chip key={value} label={value} size="small" />
                  ))}
                </Box>
              )}
              disabled={!editMode}
            >
              {setupIssueOptions.map((option) => (
                <MenuItem key={option} value={option}>
                  {option}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <TextField
            label="Setup Issue Details"
            multiline
            rows={3}
            fullWidth
            value={technicalReport.setupIssues}
            onChange={(e) => onChange('setupIssues', e.target.value)}
            disabled={!editMode}
            margin="normal"
            helperText="Free text for specific details"
          />
          <TextField
            label="Technician Notes"
            multiline
            rows={3}
            fullWidth
            value={technicalReport.technicianNotes}
            onChange={(e) => onChange('technicianNotes', e.target.value)}
            disabled={!editMode}
            margin="normal"
            helperText="Free text for detailed notes"
          />
        </Grid>
      </Grid>
    </Box>
  );
};

export default TechnicalReportSection;